import {AfterViewInit, Component, HostListener, OnInit, ViewChild} from '@angular/core';
import {SpThemingService} from '../../../services/sp-theming/sp-theming.service';
import {Router} from '@angular/router';
import {ApiUserService} from '../../../services/sp-api/sp-api-user/api-user.service';
import {User} from '../../../models/user';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {ValidationService} from '../../../services/sp-validation/validation.service';
import {ApiCompanyService} from '../../../services/sp-api/sp-api-company/api-company.service';
import {AdAccount} from '../../../models/ad-account';
import {FacebookLinkService} from '../../../services/sp-facebook/facebook-link.service';
import {environment} from '../../../../environments/environment';
import {MatDialog} from '@angular/material/dialog';
import {SpModalFacebookAccountAddComponent} from './sp-modal-facebook-account-add/sp-modal-facebook-account-add.component';
import {NavigationService} from '../../../services/sp-navigation/navigation.service';
import {LoaderService} from '../../../services/sp-loading/loader.service';
import {Company} from '../../../models/company';
import {RoleUtils} from '../../../utils/role/role-utils';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
import {AlertService} from '../../../services/sp-alert/alert.service';
import {LoggerService} from '../../../services/sp-logger/logger.service';
import {SpAccountUserEditImageComponent} from './sp-account-user-edit-image/sp-account-user-edit-image.component';
import * as Highcharts from 'highcharts';
import {ChartService} from '../../../services/sp-chart/chart.service';
import {ProjectManagerService} from '../../../services/sp-project/project-manager.service';
import {TrackerService} from '../../../services/sp-tracker/tracker.service';
import { SessionProviderService } from 'src/app/services/sp-authentication/session-provider.service';
import {Subscription} from '../../../models/subscription';

@Component({
  selector: 'app-sp-user-account-edit',
  templateUrl: './sp-account-user-edit.component.html',
  styleUrls: ['./sp-account-user-edit.component.scss']
})

export class SpAccountUserEditComponent implements OnInit, AfterViewInit {
  first_name: any;
  userEmail: string;
  dataHasChanged = false;
  loadedUser: User = undefined;
  // Facebook connect related
  adAccounts: Array<AdAccount> = [];
  fbConnectionPending: boolean = false;
  // fbConnection & fbBusinessConnection => null = default, false = error, true = success
  fbConnection: boolean = null;
  validAdAccounts: boolean = null;
  fbAccountImage: string = null;
  fbAccountName: string = null;
  //. ---
  selectedImage: any = null;
  env = environment;
  subscriptionStartDate: UntypedFormControl;
  subDuration = 12;
  createdCompany: Company = null;
  userForm: UntypedFormGroup = new UntypedFormGroup({});
  // credits + credits_extra, the only figure a customer needs.
  creditBalance = 0;
  nbrUsers = 0;
  editMode = false;

  updatingInfo = false;

  emailChanged = false;
  passwordChanged = false;

  freemium = false;
  trial = false;
  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
  @ViewChild(MatSort, {static: true}) sort: MatSort;

  // Charts
  private chartUsers: Highcharts.Chart;

  // Seed
  seedSelection: number[] = [8247, 4712, 6569, 5153, 5165];
  seedSelected?: number;

  // Constructor.
  constructor(private _spTheming: SpThemingService,
              private router: Router,
              private apiUser: ApiUserService,
              private apiCompany: ApiCompanyService,
              private validation: ValidationService,
              private dialog: MatDialog,
              private formBuilder: UntypedFormBuilder,
              private navigation: NavigationService,
              private loader: LoaderService,
              private fb: FacebookLinkService,
              private chart: ChartService,
              private alert: AlertService,
              private logger: LoggerService,
              private tracker: TrackerService,
              private pm: ProjectManagerService,
              protected sessionProvider: SessionProviderService) {
    // Set the theme to use.
    this._spTheming.setSpThemeName('dashboard-desk');

    // Loading actual normalization seed if any
    this.seedSelected = this.pm.seed;
  }
  // On init.
  ngOnInit() {
    this.loader.load();
    this.navigation.adBlockDetectionLaunch().then();

    if (this.sessionProvider.activeSession?.user) {
      this.freemium = this.sessionProvider.activeSession.user.role.level <= RoleUtils.freemiumLevel;
      this.trial = this.sessionProvider.activeSession.user.role.level == RoleUtils.trialLevel
    }

    this.userForm = this.formBuilder.group({
      first_name: ['', [
        Validators.required,
        Validators.maxLength(50)
      ]],
      last_name: ['', [
        Validators.required,
        Validators.maxLength(50)
      ]],
      passwordChange: ['', [
        Validators.minLength(10),
        Validators.pattern(/^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9]).{10,}$/)
      ]],
      passwordConfirm: [''],
      company: ['']
    }, {
      validators: this.validation.passwordMatchValidator('passwordChange', 'passwordConfirm')
    });

    this.userForm.controls['passwordConfirm'].disable();

    this.subscriptionStartDate = new UntypedFormControl(new Date(), [
      Validators.required
    ]);
  }

  ngAfterViewInit() {
    this.apiUser.getOne(this.sessionProvider.activeSession?.user?.id).then(user => {
      this.loadedUser = user;

      this.apiCompany.getOne(this.loadedUser.company.id).then(company => {
        //Loading details of the company
        this.adAccounts = user.ad_accounts;
        this.refreshFbScopeChecks(false);
        this.loadedUser.company = company;

        const subscription = this.loadedUser.company.subscription;
        // One figure, not two: the monthly and the extra balance have different lifecycles
        // but a customer only cares how many reports they can still launch, and reading a
        // "0" next to a non-empty second balance is what made them stop using the product.
        // Shown as a figure rather than a ring, because a balance has no total to be a
        // proportion of — a filled ring next to the proportional users one read as "100%".
        this.creditBalance = Subscription.totalCredits(subscription);
        let userLeft = subscription.users - this.loadedUser.company.users.length;
        this.chartUsers = Highcharts.chart('container-2',this.chart.createUserCreditsPieChart(userLeft,subscription.users,"Users",userLeft + " users left",true));

        this.initUserForm(this.loadedUser);

        this.loader.loaded();
        this.loadCompanyInfo(user.company);
      })
    });
  }

  @HostListener('window:resize')
  onResize() {
    this.chartUsers?.reflow();
  }

  // Null for subscriptions that never renew, which is the whole fixed-allocation cohort.
  // Merging the two balances into one figure otherwise hides that part of it is monthly
  // and gets reset rather than accumulating.
  get renewalDate(): Date | null {
    return this.loadedUser?.company?.subscription?.next_credit_update ?? null;
  }

  onSeedSelectionChange(seed: number) {
    this.seedSelected = seed;
    this.pm.seed = this.seedSelected;
  }

  navigateToNewProject(){
    if (this.freemium == true || this.trial) {
      this.router.navigateByUrl('/projects/create/select').then();
    } else if(this.fbConnection && this.validAdAccounts && this.sessionProvider.activeSession?.user?.ad_accounts?.length > 0){
      this.router.navigateByUrl('/projects/create/select').then();
    }
  }

  private refreshFbScopeChecks(refreshAdAccounts: boolean = true) {
    this.fb.isLogged().then(logged => {
      if (logged) {
        const refreshAdAccountsPromise = refreshAdAccounts ? this.refreshUserAdAccounts() : Promise.resolve();
        refreshAdAccountsPromise.then(_ => {
          this.fb.checkAllScopesAreValid().then(scopesOk => {
            const mustRefresh = this.sessionProvider.activeSession?.user?.has_invalid_ad_accounts;
            this.validAdAccounts = this.adAccounts.length > 0;
            this.fbConnection = !mustRefresh && scopesOk;
          });
        });

        this.refreshFbUserPicture().then();
      } else {
        this.validAdAccounts = false;
        this.fbConnection = localStorage.getItem(FacebookLinkService.LOCAL_CHECK_KEY) === 'disconnected' ? false : null;
      }
    });
  }

  facebookGetStatus(): void {
    this.logger.debug("Local storage FB info: ", localStorage.getItem(FacebookLinkService.LOCAL_KEY));
    this.fb.getLoginStatus().then(resp => this.logger.debug(resp));
  }

  async facebookBusinessLogin() {
    if (this.fbConnectionPending) return;
    this.fbConnectionPending = true;
    const wasConnectedBefore = this.fbConnection;
    try {
      const resp = await this.fb.login();
      
      this.refreshFbScopeChecks();
      this.logger.debug(resp);

      // Track successful Facebook connection (only if it's a new connection)
      if (resp.status === 'connected' && !wasConnectedBefore) {
        this.tracker.track('facebook_account_connected', {});
      }

      return resp;
    } catch (err) {
      throw err;
    } finally {
      this.fbConnectionPending = false;
    }
  }

  facebookLogout(): void {
    this.fb.logout().then(() => {
      this.fbAccountImage = null;
      this.fbAccountName = null;
      this.refreshFbScopeChecks();
    });
  }

  facebookUnlink(): void {
    this.fb.unlink().then(resp => {
      // Token must be refreshed when session is invalidated
      if (this.sessionProvider.activeSession?.user) this.sessionProvider.activeSession.user.has_invalid_ad_accounts = true;
      this.logger.debug(resp);
    });
  }

  facebookApiTestCall(): void {
    this.fb.getUserInfo().then(resp => this.logger.debug(resp)).catch(err => this.logger.logError(err, 1, err));
  }

  async refreshUserAdAccounts() {
    try {
      const accounts = await this.fb.getAdAccounts();
      const usedAccounts = this.loadedUser.ad_accounts.filter(a => a.selected).map(a => a.facebook_id);
      this.adAccounts = accounts.filter(a => usedAccounts.includes(a.facebook_id));
      this.loadedUser.ad_accounts = this.adAccounts;
      await this.updateAdAccounts();
      if (this.sessionProvider.activeSession?.user) this.sessionProvider.activeSession.user.has_invalid_ad_accounts = false;
    }
    catch (error) {
      this.logger.logError('Unable to get ad accounts from FB', 3, error);
    }
  }

  async refreshFbUserPicture() {
    this.fbAccountImage = await this.fb.getUserPicture();
  }

  resetData() {
    this.initUserForm(this.loadedUser);
    this.dataHasChanged = false;
  }


  loadCompanyInfo(company: Company) {
    this.subscriptionStartDate.setValue(company.subscription.contract_start_at);

    this.nbrUsers = company.subscription.users;

    let startAt = new Date(company.subscription.contract_start_at);
    let endAt = new Date(company.subscription.contract_expire_at);

    let months: number;
    months = (endAt.getFullYear() - startAt.getFullYear()) * 12;
    months -= startAt.getMonth();
    months += endAt.getMonth();
    months = months <= 0 ? 0 : months;
    this.subDuration = months;

    //this.selectedImage = company.image === null ? null : this.env.values.api.storageUrl + company.image;

    this.createdCompany = company;
    this.editMode = true;
  }

  imageChanged = false;
  openUserImageDialog(): void {
    this.dialog.open(SpAccountUserEditImageComponent,{
      autoFocus: false,
      minHeight:'10rem',
      minWidth:'40rem',
      maxHeight:'60rem',
      maxWidth:'60rem',
    }).afterClosed().subscribe(result => {
      if (result) {
        if (typeof result === 'boolean') {
          if (!this.loadedUser.fb_picture || this.loadedUser.image !== this.fbAccountImage) {
            this.loadedUser.fb_picture = true;
            this.loadedUser.image = this.fbAccountImage;
            this.selectedImage = this.fbAccountImage;
            this.dataHasChanged = true;
          }
        } else {
          this.loadedUser.fb_picture = false;
          this.onImageChange(result);
          this.dataHasChanged = true;
        }
      }
    });
  }

  firstNameChanged = false;
  lastNameChanged = false;

  updateUserInfo() {
    if (!this.userForm.valid) {
      return;
    }
    this.updatingInfo = true;
    let user = this.buildUserFromForm();
    this.apiUser.updateOne(user)
      .then(async user => {
        await this.sessionProvider.refreshSession();
        this.adAccounts = user.ad_accounts;

        const companyDetails = this.loadedUser.company;
        this.loadedUser = user;
        this.loadedUser.company = companyDetails;
        this.alert.notify('Success', 'Your profile has been updated', 'success');

      })
      .finally(() =>
        this.updatingInfo = false
      );
    this.dataHasChanged = false;
    this.userForm.get('passwordChange').setValue('');
    this.userForm.get('passwordConfirm').setValue('');
    this.userForm.get('passwordConfirm').disable();
  }

  openAdAccountDialog(): void {
    const dialogRef = this.dialog.open(SpModalFacebookAccountAddComponent,{
      maxHeight:'32rem',maxWidth:'40rem', minWidth:'40rem',
      data: this.adAccounts
    });

    dialogRef.afterClosed().subscribe(adAccounts => {
      if (adAccounts) {
        if (adAccounts.updated) {
          this.adAccounts = adAccounts.accounts === undefined ? this.adAccounts : adAccounts.accounts;
          if (this.sessionProvider.activeSession?.user) this.sessionProvider.activeSession.user.ad_accounts = this.adAccounts;
          if (this.adAccounts.length > 0) this.validAdAccounts = true;
          else this.validAdAccounts = null;
          this.updateAdAccounts().then(() => {
            this.alert.notify('Success', 'Your ad accounts have been updated successfully', 'success');
          }).catch(() => {
            this.alert.notify('Error', 'An error occurred while updating ad accounts, this has been reported', "error");
          });
        }
      }
    });
  }

  initUserForm(user: User) {
    let laravelEmailParams: Map<string, string> = new Map();
    laravelEmailParams.set('own_email', user.email);

    this.userForm.get('first_name').setValue(user.first_name);
    this.userForm.get('last_name').setValue(user.last_name);
    this.userForm.get('company').setValue(user.company.name);

    this.userEmail = user.email;

    this.userForm.get('company').disable();

    this.adAccounts.forEach(ad => ad.selected = true);

    if (this.loadedUser.image) {
      this.selectedImage = this.env.config.api.storageUrl + this.loadedUser.image;
    }
  }

  buildUserFromForm() {
    let email = this.userEmail;
    let firstName = this.userForm.get('first_name').value;
    let lastName = this.userForm.get('last_name').value;
    let password = this.userForm.get('passwordChange').value;

    let user = new User(email, firstName, lastName);
    if (password !== "") {
      user.password = password;
    }
    user.id = this.loadedUser.id;
    user.ad_accounts = this.adAccounts;
    user.image = this.selectedImage;
    user.fb_picture = this.loadedUser.fb_picture;
    return user;
  }

  async updateAdAccounts() {
    try {
      const ads = await this.apiUser.updateAdAccounts(this.loadedUser, this.adAccounts);
      this.tracker.track('user_profile_ad_accounts_updated', {count: ads.length});
      this.adAccounts = ads;
    } catch (err) {
      this.logger.logError('Error while updating ad accounts', 3, err);
      throw err;
    }
  }

  onImageChange(imageInput: HTMLInputElement) {
    const file: File = imageInput.files[0];
    const reader = new FileReader();

    reader.addEventListener('load', (event: any) => {
      this.selectedImage = event.target.result;
      this.imageChanged = true;
      this.dataChangeCheck();
    });

    reader.readAsDataURL(file);
  }

  dataChangeCheck():void{
    if (this.firstNameChanged || this.lastNameChanged || this.emailChanged || this.passwordChanged || this.imageChanged) {
      this.dataHasChanged = true;
    } else {
      this.dataHasChanged = false;
    }
  }
  onFirstNameChange(searchValue: string): void {
    if (this.sessionProvider.activeSession?.user?.first_name === searchValue) {
      this.firstNameChanged = false;
    } else {
      this.firstNameChanged = true;
    }
    this.dataChangeCheck();
  }

  onLastNameChange(searchValue: string): void {
    if(this.sessionProvider.activeSession?.user?.last_name === searchValue) {
      this.lastNameChanged = false;
    } else {
      this.lastNameChanged = true;
    }
    this.dataChangeCheck();
  }

  onPasswordConfirmChange(password: any, confirmPassword: any): void {
    this.passwordChanged = password === confirmPassword;
    this.dataChangeCheck();
  }

  onPasswordChange(password: string) {
    if (password && password !== '') this.userForm.get('passwordConfirm').enable();
    else this.userForm.get('passwordConfirm').disable();
  }

  protected readonly undefined = undefined;
}
